import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import selectLayout from '@/features/home-videos/utils/selectLayout';
import { videoShape } from './videoShape';
import FeaturedVideoCard from './FeaturedVideoCard';
import VideoRowCard from './VideoRowCard';
import LearningHubLink from './LearningHubLink';

/**
 * Presentational "Short videos to show you around" section. The layout
 * follows the number of videos (see selectLayout); renders nothing for none.
 */
HomeVideosSection.propTypes = {
  videos: PropTypes.arrayOf(videoShape).isRequired,
  onWatch: PropTypes.func.isRequired,
  learningHubUrl: PropTypes.string
};

export const sectionClassName = 'w-full bg-white border border-cs-gray-100 rounded-[15px] shadow-cs-flat p-4';

export function SectionHeader(props) {
  const { learningHubUrl } = props;
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-4 text-cs-gray-900">
      <h2
        id="home-videos-heading"
        className="md:col-start-1 md:row-start-1 self-center font-semibold text-xl leading-6"
      >
        {t('home_videos.section.heading')}
      </h2>
      <p className="!mb-0 md:col-start-1 md:row-start-2 font-normal text-lg leading-normal tracking-[0.18px]">
        {t('home_videos.section.subheading')}
      </p>
      {learningHubUrl && (
        <div className="justify-self-start md:col-start-2 md:row-start-1 md:justify-self-end self-center">
          <LearningHubLink url={learningHubUrl} />
        </div>
      )}
    </div>
  );
}

SectionHeader.propTypes = {
  learningHubUrl: PropTypes.string
};

export default function HomeVideosSection(props) {
  const { videos, onWatch, learningHubUrl } = props;
  const layout = selectLayout(videos.length);

  if (layout === 'none') return null;

  let cards;
  if (layout === 'single') {
    cards = <FeaturedVideoCard video={videos[0]} onWatch={onWatch} orientation="horizontal" />;
  } else if (layout === 'pair') {
    cards = (
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {videos.map((video) => (
          <li key={video.id}>
            <FeaturedVideoCard video={video} onWatch={onWatch} fillHeight />
          </li>
        ))}
      </ul>
    );
  } else {
    const [featured, ...rest] = videos;
    // minmax(0, ...) lets the columns shrink below their content's min-content
    // width; a plain 1fr track grows to fit a truncated (nowrap) resource
    // title and pushes the featured card past the screen edge.
    cards = (
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-3 items-start">
        <FeaturedVideoCard video={featured} onWatch={onWatch} />
        <ul className="flex flex-col gap-3">
          {rest.map((video) => (
            <li key={video.id}>
              <VideoRowCard video={video} onWatch={onWatch} />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <section
      aria-labelledby="home-videos-heading"
      data-testid="home-videos"
      data-layout={layout}
      className={sectionClassName}
    >
      <SectionHeader learningHubUrl={learningHubUrl} />
      <div className="mt-4">{cards}</div>
    </section>
  );
}
